import React, { Component } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import url from 'url';
import lodashCompact from 'lodash/fp/compact';

import { getListingUrlPath, makeListingFullAddressString } from 'common/listingHelpers';

import { getHelpCardTitle } from 'common/helpCardHelpers';

import { hasRoleAdmin, hasRoleLandlord, hasRolePartner, hasRoleAnonymous } from 'common/userRoles';

import {
  LISTING_STATUS_ACTIVE,
  LISTING_LINKS_TO_SCRAPE,
  LISTING_ACTIVE_STATUS_FOR_TOUR_HEALTH,
} from 'common/listingConstants';

import { JOB_STATUS_COMPLETED } from 'common/jobConstants';

import {
  MARKETING_PARTNER_LISTING_STATUS_COMPLETED,
  MARKETING_PARTNER_LISTING_STATUS_IN_PROGRESS,
  MARKETING_PARTNER_LISTING_STATUS_WITHDRAWN,
} from 'common/marketingPartnerListingConstants';

import { DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS } from 'common/dataTableConstants';

import {
  PARTNER_TASK_CATEGORY_PRICING,
  PARTNER_TASK_STATUS_SENT,
} from 'common/partnerTaskConstants';

import ListingActions from 'client/actions/Listing';
import withLocationQuery from 'client/helpers/withLocationQuery';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrPartnerSection from 'client/components/SrPartnerSection';
import SrPartnerPageFooter from 'client/components/SrPartnerPageFooter';
import SrPartnerListingHeader from 'client/components/SrPartnerListingHeader';
import SrPartnerListingStatsV2 from 'client/components/SrPartnerListingStatsV2';
import SrPartnerListingOccupancyInfo from 'client/components/SrPartnerListingOccupancyInfo/SrPartnerListingOccupancyInfo';
import SrPartnerListingAccessInstructions from 'client/components/SrPartnerListingAccessInstructions/SrPartnerListingAccessInstructions';
import SrListingLowerPriceModal from 'client/components/SrListingLowerPriceModal/SrListingLowerPriceModal';
import SrPartnerListingTourReviews from 'client/components/SrPartnerListingTourReviews';
import SrDataTablePartnerHouseholdMoveIns from 'client/components/SrDataTablePartnerHouseholdMoveIns';
import SrPartnerListingHouseholdsApplying from 'client/components/SrPartnerListingHouseholdsApplying/SrPartnerListingHouseholdsApplying'; // eslint-disable-line max-len
import SrPartnerMarketingLinksModal from 'client/components/SrPartnerMarketingLinksModal';
import SrPartnerListingAppliancesIncludedSection from 'client/components/SrPartnerListingAppliancesIncludedSection';
import SrPartnerContactSupportModal from 'client/components/SrPartnerContactSupportModal';
import SrPartnerShareListingModal from 'client/components/SrPartnerShareListingModal';
import SrPartnerList from 'client/components/SrPartnerList';
import SrToursHealth from 'client/components/SrToursHealth/SrToursHealth';
import SrDataTableMarketingPartnerListingsCard from 'client/components/SrDataTableMarketingPartnerListingsCard';
import SrPartnerMarketListingEditFieldsModal from 'client/components/SrPartnerMarketListingEditFieldsModal';
import SrPartnerMarketListingModal, {
  FORM_TYPE_COMPLETE_PRELEASING_LISTING,
  FORM_TYPE_UPDATE_PRELEASING_LISTING,
} from 'client/components/SrPartnerMarketListingModal';
import SrPartnerMarketListingEditDueDateModal from 'client/components/SrPartnerMarketListingEditDueDateModal';
import SrPartnerMarketingListingStatusUpdateModal from 'client/components/SrPartnerMarketingListingStatusUpdateModal';
import SrAdminComparativeMarketAnalysisForm from 'client/components/SrAdminComparativeMarketAnalysisForm/SrAdminComparativeMarketAnalysisForm';
import SrSimpleContactCard from 'client/components/SrSimpleContactCard/SrSimpleContactCard';
import SrHelpCardSection from 'client/components/SrHelpCardSection/SrHelpCardSection';
import SrPartnerContactAccountManager from 'client/components/SrPartnerContactAccountManager';
import SrDataTablePartnerListingHistoricalUpdates from 'client/components/SrDataTablePartnerListingHistoricalUpdates';
import SrAdminListingNotes from 'client/components/SrAdminListingNotes';
import SrCard, { SR_CARD_BULLET_COLOR_BLUE } from 'client/components/SrCard/SrCard';
import SrGoingToMarketLiveCard from 'client/components/SrGoingToMarketLiveCard/SrGoingToMarketLiveCard';
import SrPriceMarketFitSunSpot from 'client/components/SrPriceMarketFitSunSpot/SrPriceMarketFitSunSpot';
import SrPartnerListingPagePropertyConditionReport from 'client/components/SrPartnerListingPagePropertyConditionReport/SrPartnerListingPagePropertyConditionReport';
import SrPartnerListingPagePropertyConditionReportForm from 'client/components/SrPartnerListingPagePropertyConditionReport/SrPartnerListingPagePropertyConditionReportForm';
import SrLeadHealthCard from 'client/components/SrLeadHealthCard/SrLeadHealthCard';
import SrMonitorErrorCard from 'client/components/SrMonitorErrorCard/SrMonitorErrorCard';
import SrLeadOutcomes from 'client/components/SrLeadOutcomes/SrLeadOutcomes';
import SrPreLeaseStats from 'client/components/SrPreLeaseStats/SrPreLeaseStats';
import SrConcessionsPro from 'client/components/SrConcessionsPro/SrConcessionsPro';
import SrPartnerSunroomNotes from 'client/components/SrPartnerSunroomNotes';
import SrPartnerTaskList from 'client/components/SrPartnerTaskList/SrPartnerTaskList';
import SrPartnerLeadsCard from 'client/components/SrPartnerLeadsCard/SrPartnerLeadsCard';
import SrPriceMarketFitSunSpotPartnerTask from 'client/components/SrPriceMarketFitSunSpot/SrPriceMarketFitSunSpotPartnerTask';
import SrComparativeMarketAnalysisCard from 'client/components/SrComparativeMarketAnalysisCard/SrComparativeMarketAnalysisCard';
import SrDaysOnMarketComparativeMarketAnalysisCard from 'client/components/SrDaysOnMarketComparativeMarketAnalysisCard/SrDaysOnMarketComparativeMarketAnalysisCard';
import SrUnderwrittenRentComparisonCard from 'client/components/SrUnderwrittenRentComparisonCard/SrUnderwrittenRentComparisonCard';

import branch from 'client/core/branch';

import { actionDataTableLoadItems } from 'client/helpers/dataTables';

import { PARTNER_TYPE_AGENT } from 'common/partnerConstants';

const getPricingPartnerTask = (partnerTasks) => {
  const pricingPartnerTask = partnerTasks.find((task) => {
    return (
      task.category === PARTNER_TASK_CATEGORY_PRICING && task.status === PARTNER_TASK_STATUS_SENT
    );
  });

  return pricingPartnerTask;
};
@branch(({ match }) => ({
  currentUser: ['currentUser'],
  currentPartnerUser: ['partners', 'currentPartnerUser'],
  partnersBySlug: ['partners', 'bySlug'],
  listingLinksByListingId: ['listingLinksByListingId'],
  listing: ['listing'],
  appUrl: ['env', 'APP_URL'],
  partner: ['partners', 'bySlug', match.params.partnerSlug],
  tourHealth: ['listingTourHealth', match.params.listingId],
  leadHealth: ['listingLeadHealth', match.params.listingId],
  scheduledLeadToursCount: ['scheduledLeadToursCountByListingId', match.params.listingId],
  userAgent: ['analytics', 'userAgent'],
}))
@withLocationQuery
class SrPartnerListingPageListingFullService extends Component {
  static propTypes = {
    currentUser: PropTypes.object.isRequired,
    currentPartnerUser: PropTypes.object,
    partnersBySlug: PropTypes.object,
    listings: PropTypes.array,
    concessions: PropTypes.array,
    marketingListings: PropTypes.array,
    listingLinksByListingId: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
    reloadPartnerListing: PropTypes.func.isRequired,
    appUrl: PropTypes.string.isRequired,
    history: PropTypes.object.isRequired,
    householdsApplying: PropTypes.object,
    location: PropTypes.object.isRequired,
    helpCards: PropTypes.array,
    leadOutcomes: PropTypes.object,
    partnerTasks: PropTypes.array,
    scheduledLeadToursCount: PropTypes.number,
  };

  constructor(props) {
    super(props);
    this.state = {
      listingLinksModalIsOpen: false,
      withdrawalSupportModalIsOpen: false,
      supportLinksModalIsOpen: false,
      shareListingModalIsOpen: false,
      lowerPriceModalIsOpen: false,
      marketingPropertyListingModalProps: null,
      marketingPropertyListingAgentModalProps: null,
      marketingPropertyListingEditDueDateModalProps: null,
    };
    this.applicationsRef = React.createRef();
  }

  componentDidMount() {
    const { history, match, location, dispatch, currentUser } = this.props;

    const isAdmin = hasRoleAdmin(currentUser.roles);
    const queryUrl = queryString.parse(location.search);
    const listingId = parseInt(match.params.listingId, 10);

    if (queryUrl.showLowerPrice) {
      this.setState({ lowerPriceModalIsOpen: true });
      history.replace(match.url);
    }

    if (queryUrl.showUpcomingMoveIns) {
      this._scrollToUpcomingMoveInsSection();
    }

    if (queryUrl.showPendingApprovalApplications) {
      this._scrollToPendingApprovalApplications();
    }

    if (queryUrl.updatePrelease) {
      this._scrollToUpdatePrelease();
    }

    if (isAdmin) {
      dispatch(ListingActions.getLeadHealth, {
        listingId: parseInt(match.params.listingId, 10),
      });
    }

    dispatch(ListingActions.getTourHealth, {
      listingId: match.params.listingId,
    });

    dispatch(ListingActions.getScheduledLeadToursCount, {
      listingId: listingId,
    });
  }

  _onCMACardRef = (el) => {
    this._cMACardRef = el;
  };

  _onEditListingClick = () => {
    const { partnersBySlug, listings, match, history } = this.props;
    const [listing] = listings;
    const partner = partnersBySlug[match.params.partnerSlug];
    history.push(`/partner/${partner.slug}/listing/${listing.id}/edit`);
  };

  _onOpenListingLinksModal = () => {
    this.setState({
      listingLinksModalIsOpen: true,
    });
  };

  _onCloseListingLinksModal = () => {
    this.setState({
      listingLinksModalIsOpen: false,
    });
  };

  _onOpenWithdrawalSupportModal = () => {
    this.setState({
      withdrawalSupportModalIsOpen: true,
    });
  };

  _onCloseWithdrawalSupportModal = () => {
    this.setState({
      withdrawalSupportModalIsOpen: false,
    });
  };

  _onOpenShareListingModal = () => {
    this.setState({
      shareListingModalIsOpen: true,
    });
  };

  _onCloseShareListingModal = () => {
    this.setState({
      shareListingModalIsOpen: false,
    });
  };

  _onOpenSupportLinksModal = () => {
    this.setState({
      supportLinksModalIsOpen: true,
    });
  };

  _onCloseSupportLinksModal = () => {
    this.setState({
      supportLinksModalIsOpen: false,
    });
  };

  _onCloseLowerPriceModal = () => {
    this.props.reloadPartnerListing();
    this.setState({
      lowerPriceModalIsOpen: false,
    });
  };

  _onEditMarketingPartnerListing = (marketingPartnerListing, formType) => {
    const { partnersBySlug, match } = this.props;
    const partner = partnersBySlug[match.params.partnerSlug];
    if (partner.type === PARTNER_TYPE_AGENT) {
      this.setState({
        marketingPropertyListingAgentModalProps: {
          marketingPartnerListing,
          partner: marketingPartnerListing.partner,
        },
      });
    } else {
      const marketingPropertyListingModalProps = {
        marketingPartnerListing,
        partner: marketingPartnerListing.partner,
      };

      if (formType) {
        marketingPropertyListingModalProps.formType = formType;

        if (formType === FORM_TYPE_COMPLETE_PRELEASING_LISTING) {
          marketingPropertyListingModalProps.isNew = false;
        }
      }

      this.setState({
        marketingPropertyListingModalProps: marketingPropertyListingModalProps,
      });
    }
  };

  _onEditMarketingPartnerListingDueDate = (marketingPartnerListing) => {
    this.setState({
      marketingPropertyListingEditDueDateModalProps: {
        marketingPartnerListing,
      },
    });
  };

  _onCloseAgentPartnerMarketListingModal = () => {
    this.setState({
      marketingPropertyListingAgentModalProps: null,
    });
  };

  _onClosePartnerMarketListingEditDueDateModal = () => {
    this.setState({
      marketingPropertyListingEditDueDateModalProps: null,
    });
  };

  _onReloadDataTableMarketingPartnerListing = (marketingPartnerListing) => {
    const { dispatch } = this.props;

    this.props.reloadPartnerListing();
    dispatch(actionDataTableLoadItems, {
      dataSource: DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS,
      viewId: `by-marketing-partner-listing-id-${marketingPartnerListing.id}`,
      filter: {
        'marketingPartnerListings.id': marketingPartnerListing.id,
      },
      count: 1,
    });
  };

  _onEditMarketingPartnerListingFields = (marketingPartnerListing, fields) => {
    this.setState({
      marketingPropertyListingEditFieldsModalProps: {
        marketingPartnerListing,
        ...fields,
      },
    });
  };

  _onClosePartnerMarketListingEditFieldsModal = () => {
    this.setState({
      marketingPropertyListingEditFieldsModalProps: null,
    });
  };

  _onClosePartnerMarketListingModal = () => {
    this.setState({
      marketingPropertyListingModalProps: null,
    });
  };

  _onFeedbackSectionRef = (feedbackSectionRef) => {
    const { location } = this.props;

    if (location.search.includes('scrollTo=feedback') && feedbackSectionRef) {
      feedbackSectionRef.scrollIntoView({ behavior: 'smooth' });
    }
  };

  _scrollToPendingApprovalApplications = () => {
    setTimeout(() => {
      const element = global.document.querySelector('.HouseholdsApplyingSection');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', alignToTop: true });
      }
    }, 1000);
  };

  _scrollToUpcomingMoveInsSection = () => {
    setTimeout(() => {
      const element = global.document.querySelector('.UpcomingMoveInsSection');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', alignToTop: true });
      }
    }, 1000);
  };

  _scrollToUpdatePrelease = () => {
    setTimeout(() => {
      const element = global.document.querySelector('.preleasingTimeline');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 1000);
  };

  _onBack = () => {
    const { history, match } = this.props;

    history.push(`/partner/${match.params.partnerSlug}`);
  };

  _shouldDisplayTourHealth = () => {
    const { listings, currentUser, marketingListings } = this.props;

    const isAdmin = hasRoleAdmin(currentUser.roles);
    const listing = (listings && listings[0]) || null;
    const marketingListing = (marketingListings && marketingListings[0]) || null;

    if (!listing) {
      return false;
    }

    return (
      LISTING_ACTIVE_STATUS_FOR_TOUR_HEALTH.includes(listing.status) &&
      (marketingListing?.sunlock || listing.codeboxLockId) &&
      isAdmin
    );
  };

  _getTourHealthComponent = () => {
    const { tourHealth, listings } = this.props;

    const shouldDisplayTourHealth = this._shouldDisplayTourHealth();
    if (!shouldDisplayTourHealth) {
      return null;
    }
    if (!tourHealth) {
      return <SrMonitorErrorCard monitorName="Tour Health" />;
    }
    return <SrToursHealth tourHealth={tourHealth} listing={(listings && listings[0]) || null} />;
  };

  shouldDisplayListingVitals = () => {
    const { leadHealth } = this.props;

    return this._shouldDisplayTourHealth() || leadHealth;
  };

  _scrollToCMACard = () => {
    if (this._cMACardRef) {
      this._cMACardRef.setShouldCollapseCMA(false);
    }

    setTimeout(() => {
      const element = global.document.querySelector('.srComparativeMarketAnalysisCard');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', alignToTop: true });
      }
    }, 1000);
  };

  render() {
    const {
      currentUser,
      currentPartnerUser,
      partnersBySlug,
      listings,
      marketingListings,
      listingLinksByListingId,
      match,
      dispatch,
      appUrl,
      householdsApplying,
      listingTourReviews,
      history,
      location,
      helpCards,
      reloadPartnerListing,
      leadHealth,
      leadOutcomes,
      preLease,
      locationQuery,
      concessions,
      partnerTasks,
      scheduledLeadToursCount,
      userAgent,
    } = this.props;
    const {
      listingLinksModalIsOpen,
      withdrawalSupportModalIsOpen,
      supportLinksModalIsOpen,
      shareListingModalIsOpen,
      lowerPriceModalIsOpen,
      marketingPropertyListingEditFieldsModalProps,
      marketingPropertyListingModalProps,
      marketingPropertyListingAgentModalProps,
      marketingPropertyListingEditDueDateModalProps,
    } = this.state;

    const currentPartner = partnersBySlug[match.params.partnerSlug];
    const listing = (listings && listings[0]) || null;
    const marketingListing = (marketingListings && marketingListings[0]) || null;
    const listingLinks = (listing && listingLinksByListingId[listing.id]) || [];
    const isAdmin = hasRoleAdmin(currentUser.roles);
    const isLandlord = hasRoleLandlord(currentUser.roles);
    const isPartner = hasRolePartner(currentUser.roles);
    const isAnonymous = hasRoleAnonymous(currentUser.roles);
    const listingLinksSubset = [
      {
        siteName: 'Sunroom Rentals',
        linkUrl: listing ? `${url.resolve(appUrl, getListingUrlPath(listing))}` : '',
      },
    ].concat(
      LISTING_LINKS_TO_SCRAPE.filter(({ siteName }) => siteName !== 'MLS' || listing?.postToMls)
        .map(({ siteName }) => {
          const listingLink = listingLinks.find((link) => siteName === link.siteName) || {};

          return {
            siteName: siteName,
            wasReviewed: listingLink.wasReviewed,
            linkUrl: listingLink.linkUrl,
          };
        })
        .filter(({ wasReviewed }) => wasReviewed),
    );
    const showGoingToMarketStatusSection =
      marketingListing &&
      marketingListing.status !== MARKETING_PARTNER_LISTING_STATUS_COMPLETED &&
      marketingListing.status !== MARKETING_PARTNER_LISTING_STATUS_WITHDRAWN;
    const showGoingToMarketAdminSection =
      marketingListing && marketingListing.status !== MARKETING_PARTNER_LISTING_STATUS_IN_PROGRESS;
    const showPropertyConditionReport =
      marketingListing?.propertyConditionOverall != null && (isPartner || isAdmin);
    const showPropertyConditionReportForm = marketingListing && isAdmin;
    const showPropertyConditionReportSection =
      showPropertyConditionReport || showPropertyConditionReportForm;

    const shouldDisplayListingVitals = this.shouldDisplayListingVitals();
    const tourHealthComponent = this._getTourHealthComponent();
    const concessionsEnabled = !!currentPartner?.experienceConfiguration?.concessionsEnabled;
    const latestConcession = concessions[0] || null;

    const shouldDisplayCMA =
      listing.comparativeMarketAnalysisRentPrice && listing.comparativeMarketAnalysisPdfUrl;

    const pricePartnerTask = getPricingPartnerTask(partnerTasks || []);

    let statsDOM = null;
    if (preLease) {
      statsDOM = (
        <SrPreLeaseStats
          currentUser={currentUser}
          currentPartner={currentPartner}
          listing={listing}
          preLease={preLease}
          locationQuery={locationQuery}
          onUpdatePreLease={() =>
            this._onEditMarketingPartnerListing(
              marketingListing,
              FORM_TYPE_UPDATE_PRELEASING_LISTING,
            )
          }
          onEndPreLease={() =>
            this._onEditMarketingPartnerListing(
              marketingListing,
              FORM_TYPE_COMPLETE_PRELEASING_LISTING,
            )
          }
          scheduledLeadToursCount={scheduledLeadToursCount}
        />
      );
    } else {
      statsDOM = (
        <SrPartnerListingStatsV2
          listingId={listing.id}
          isUnit={!!listing.apartmentBuildingId}
          listing={listing}
          scheduledLeadToursCount={scheduledLeadToursCount}
          enableViewTexts={
            isAdmin || currentPartner.experienceConfiguration?.showLeadChatConversations
          }
          enableViewTours={isAdmin}
        />
      );
    }

    return (
      <>
        {listing && currentPartner && (
          <div>
            <SrPartnerListingHeader
              listing={listing}
              currentUser={currentUser}
              currentPartnerUser={currentPartnerUser}
              currentPartner={currentPartner}
              onEditListingClick={this._onEditListingClick}
              onOpenListingLinksClick={this._onOpenListingLinksModal}
              onOpenSupportLinksClick={this._onOpenSupportLinksModal}
              onOpenShareListingClick={this._onOpenShareListingModal}
              partnerSlug={match.params.partnerSlug}
              isLandlord={isLandlord}
              dispatch={dispatch}
              onLowerRentPrice={reloadPartnerListing}
              onDOMMedianClick={this._scrollToCMACard}
            />
            <SrBox margin="0 auto" padding="0 10px" maxWidth="1180px">
              {isAdmin && (
                <SrBox marginTop="50px">
                  <SrCard
                    header="Notes (Admin-Only)"
                    color={SR_CARD_BULLET_COLOR_BLUE}
                    maxWidth="none"
                  >
                    <SrAdminListingNotes
                      listingId={listing.id}
                      dispatch={dispatch}
                      currentUser={currentUser}
                    />
                  </SrCard>
                </SrBox>
              )}
              {isAdmin && (
                <SrBox marginTop="50px">
                  <SrAdminComparativeMarketAnalysisForm
                    listing={listing}
                    dispatch={dispatch}
                    partner={currentPartner}
                    onSubmitForm={reloadPartnerListing}
                  />
                </SrBox>
              )}

              <SrPartnerSection title="Stats">{statsDOM}</SrPartnerSection>
              <SrPartnerSunroomNotes
                listingId={listing.id}
                dispatch={dispatch}
                currentUser={currentUser}
              />
              {householdsApplying && (
                <div
                  className="HouseholdsApplyingSection"
                  style={{
                    scrollMargin: '50px',
                  }}
                >
                  <SrPartnerListingHouseholdsApplying
                    partner={currentPartner}
                    listingId={listing.id}
                    householdsApplying={householdsApplying}
                    appUrl={appUrl}
                    history={history}
                    dispatch={dispatch}
                    currentUser={currentUser}
                    location={location}
                  />
                </div>
              )}
              <div
                className="UpcomingMoveInsSection"
                style={{
                  scrollMargin: '50px',
                }}
              >
                <SrDataTablePartnerHouseholdMoveIns
                  partner={currentPartner}
                  listingId={listing.id}
                  householdsApplying={householdsApplying}
                  appUrl={appUrl}
                  history={history}
                  dispatch={dispatch}
                  currentUser={currentUser}
                  upcoming
                  excludeArchivedHouseholds
                  useSrHouseholdUpcomingMoveInCard
                />
              </div>
              {listing.status !== LISTING_STATUS_ACTIVE && (
                <SrDataTablePartnerHouseholdMoveIns
                  partner={currentPartner}
                  listingId={listing.id}
                  householdsApplying={householdsApplying}
                  appUrl={appUrl}
                  history={history}
                  dispatch={dispatch}
                  currentUser={currentUser}
                  upcoming={false}
                  useSrHouseholdUpcomingMoveInCard
                />
              )}
              <SrPartnerSection title="Pricing">
                {listing.comparativeMarketAnalysisMedianDaysOnMarket > 0 && (
                  <SrDaysOnMarketComparativeMarketAnalysisCard listing={listing} />
                )}

                {listing.underwrittenRent && <SrUnderwrittenRentComparisonCard listing={listing} />}

                {pricePartnerTask && (
                  <SrPriceMarketFitSunSpotPartnerTask
                    className="fullCard"
                    dispatch={dispatch}
                    partnerTask={pricePartnerTask}
                    currentUser={currentUser}
                    listing={listing}
                  />
                )}

                {listing.daysOnMarket >= 7 && (
                  <SrPriceMarketFitSunSpot
                    listing={listing}
                    currentUser={currentUser}
                    pricePartnerTask={pricePartnerTask}
                  />
                )}

                {shouldDisplayCMA && (
                  <div
                    className="srComparativeMarketAnalysisCard"
                    style={{
                      scrollMargin: '80px',
                    }}
                  >
                    <SrComparativeMarketAnalysisCard
                      listing={listing}
                      partner={currentPartner}
                      appUrl={appUrl}
                      userAgent={userAgent}
                      dispatch={dispatch}
                      onLowerRentPrice={reloadPartnerListing}
                      isAdmin={isAdmin}
                      ref={this._onCMACardRef}
                    />
                  </div>
                )}

                {listing && <SrDataTablePartnerListingHistoricalUpdates listing={listing} />}

                {concessionsEnabled && (
                  <SrConcessionsPro
                    concession={latestConcession}
                    listingAddress={makeListingFullAddressString(listing)}
                    rentPrice={listing.price}
                    listingId={listing.id}
                    currentUser={currentUser}
                  />
                )}
              </SrPartnerSection>

              <div id="scrollHereForFeedback" ref={this._onFeedbackSectionRef}>
                <SrPartnerSection title="Tour Feedback">
                  <SrPartnerList twoColumns>
                    <SrPartnerListingTourReviews
                      tourReviews={listingTourReviews}
                      currentUser={currentUser}
                      partner={currentPartner}
                      dispatch={dispatch}
                      location={location}
                    />
                  </SrPartnerList>
                </SrPartnerSection>
              </div>

              {!isAnonymous && (
                <SrPartnerSection title="Task List">
                  <SrPartnerTaskList
                    listingId={listing.id}
                    partnerTasks={partnerTasks}
                    currentUser={currentUser}
                    dispatch={dispatch}
                  />
                </SrPartnerSection>
              )}

              {isAdmin && (
                <SrPartnerLeadsCard
                  listingId={listing.id}
                  dispatch={dispatch}
                  currentUser={currentUser}
                  partner={currentPartner}
                  location={location}
                  listingTourReviews={listingTourReviews}
                  households={lodashCompact([
                    householdsApplying?.nextQualifiedHousehold,
                    ...householdsApplying?.pendingHouseholds,
                  ])}
                />
              )}

              <SrPartnerList twoColumns>
                <SrFlex direction="col">
                  {leadOutcomes && (
                    <SrPartnerSection title="Lead Outcomes">
                      <SrLeadOutcomes archivedReasons={leadOutcomes.archivedReasons || []} />
                    </SrPartnerSection>
                  )}
                  {shouldDisplayListingVitals && (
                    <SrPartnerSection title="Listing Vitals (Admin-Only)" twoColumns>
                      {tourHealthComponent}
                      {leadHealth && <SrLeadHealthCard leadHealth={leadHealth} />}
                    </SrPartnerSection>
                  )}
                  {showPropertyConditionReportSection && (
                    <SrPartnerSection title="Property Condition Report">
                      {showPropertyConditionReport && (
                        <SrPartnerListingPagePropertyConditionReport
                          showEdit={false}
                          marketingListing={marketingListing}
                        />
                      )}
                      {showPropertyConditionReportForm && (
                        <SrPartnerListingPagePropertyConditionReportForm
                          marketingListing={marketingListing}
                          reloadPartnerListing={reloadPartnerListing}
                        />
                      )}
                    </SrPartnerSection>
                  )}
                </SrFlex>
                {listing.landlordNotificationAlert && listing.landlord && (
                  <SrPartnerSection title="Client Updates">
                    <SrSimpleContactCard
                      name={listing.landlord.name}
                      email={listing.landlord.email}
                      phone={listing.landlord.phone}
                    />
                  </SrPartnerSection>
                )}
                <SrFlex direction="col">
                  {(marketingListing?.sunlock ||
                    marketingListing?.lockboxCode ||
                    marketingListing?.comboLockInstallJob?.status === JOB_STATUS_COMPLETED ||
                    marketingListing?.supraLockInstallJob?.status === JOB_STATUS_COMPLETED ||
                    marketingListing?.codeboxLock) && (
                    <>
                      <SrPartnerSection title="Property Access">
                        <SrPartnerListingOccupancyInfo
                          listing={listing}
                          partner={currentPartner}
                          marketingPartnerListing={marketingListing}
                        />
                        <SrBox marginTop="16px" />
                        <SrPartnerListingAccessInstructions
                          listing={listing}
                          marketingPartnerListing={marketingListing}
                          currentUser={currentUser}
                        />
                      </SrPartnerSection>
                    </>
                  )}
                  {showGoingToMarketStatusSection && (
                    <SrPartnerSection title="Going-To-Market Status">
                      <SrDataTableMarketingPartnerListingsCard
                        partner={currentPartner}
                        marketingPartnerListing={marketingListing}
                        currentUser={currentUser}
                        onEdit={() => this._onEditMarketingPartnerListing(marketingListing)}
                        onEditMarketingPartnerListingDueDate={() =>
                          this._onEditMarketingPartnerListingDueDate(marketingListing)
                        }
                        onEditFields={(fields) =>
                          this._onEditMarketingPartnerListingFields(marketingListing, fields)
                        }
                        appUrl={appUrl}
                        reloadData={() => reloadPartnerListing()}
                      />
                    </SrPartnerSection>
                  )}
                </SrFlex>
              </SrPartnerList>

              {currentPartner.amenityPhotosEnabled && (
                <SrPartnerSection title="Appliances">
                  <SrPartnerListingAppliancesIncludedSection
                    listing={listing}
                    onListingPhotosLinkClick={this._onOpenListingLinksModal}
                  />
                </SrPartnerSection>
              )}
              {showGoingToMarketAdminSection && isAdmin && (
                <SrPartnerList twoColumns>
                  <SrPartnerSection title="Going to Market (Admin-Only)">
                    <SrGoingToMarketLiveCard
                      hideEdit
                      hideEditDueDate
                      hideReportDelay
                      marketingPartnerListing={marketingListing}
                      currentUser={currentUser}
                      onEdit={null}
                      onEditMarketingPartnerListingDueDate={null}
                      onEditFields={null}
                      onEditWalkThroughFields={null}
                      location={location}
                      appUrl={appUrl}
                      reloadData={() => reloadPartnerListing()}
                    />
                  </SrPartnerSection>
                </SrPartnerList>
              )}
              {helpCards && helpCards.length > 0 && (
                <SrBox marginTop="80px">
                  <SrHelpCardSection
                    helpCards={helpCards}
                    title={getHelpCardTitle({ partner: currentPartner })}
                  />
                </SrBox>
              )}

              {listing.status === LISTING_STATUS_ACTIVE && (
                <SrDataTablePartnerHouseholdMoveIns
                  partner={currentPartner}
                  listingId={listing.id}
                  householdsApplying={householdsApplying}
                  appUrl={appUrl}
                  history={history}
                  dispatch={dispatch}
                  currentUser={currentUser}
                  upcoming={false}
                  useSrHouseholdUpcomingMoveInCard
                />
              )}
              {!isAnonymous && (
                <SrFlex width="100%" justify="center" marginTop="120px">
                  <SrPartnerContactAccountManager marketingPartnerListing={marketingListing} />
                </SrFlex>
              )}
              <SrPartnerPageFooter />
            </SrBox>
          </div>
        )}
        {listingLinksModalIsOpen && (
          <SrPartnerMarketingLinksModal
            onClose={this._onCloseListingLinksModal}
            links={listingLinksSubset}
          />
        )}
        {withdrawalSupportModalIsOpen && (
          <SrPartnerContactSupportModal
            marketingPartnerListing={marketingListing}
            onClose={this._onCloseWithdrawalSupportModal}
            headline="To withdraw this listing, contact your dedicated Account Manager"
          />
        )}
        {shareListingModalIsOpen && (
          <SrPartnerShareListingModal
            appUrl={appUrl}
            listingId={marketingListing.listingId}
            location={location}
            onClose={this._onCloseShareListingModal}
          />
        )}
        {supportLinksModalIsOpen && (
          <SrPartnerContactSupportModal
            marketingPartnerListing={marketingListing}
            onClose={this._onCloseSupportLinksModal}
          />
        )}
        {lowerPriceModalIsOpen && listing && (
          <SrListingLowerPriceModal
            listing={listing}
            dispatch={dispatch}
            onClose={this._onCloseLowerPriceModal}
          />
        )}
        {marketingPropertyListingModalProps && (
          <SrPartnerMarketListingModal
            {...marketingPropertyListingModalProps}
            isNew={false}
            partner={currentPartner}
            reloadDataTableMarketingPartnerListing={() =>
              this._onReloadDataTableMarketingPartnerListing(marketingListing)
            }
            onClose={this._onClosePartnerMarketListingModal}
            currentUser={currentUser}
          />
        )}
        {marketingPropertyListingAgentModalProps && (
          <SrPartnerMarketingListingStatusUpdateModal
            {...marketingPropertyListingAgentModalProps}
            onWithdrawClick={() =>
              this.setState({
                withdrawalSupportModalIsOpen: true,
                marketingPropertyListingAgentModalProps: null,
              })
            }
            onClose={this._onCloseAgentPartnerMarketListingModal}
            appUrl={appUrl}
          />
        )}
        {marketingPropertyListingEditFieldsModalProps && (
          <SrPartnerMarketListingEditFieldsModal
            {...marketingPropertyListingEditFieldsModalProps}
            dispatch={dispatch}
            reloadDataTableMarketingPartnerListing={() =>
              this._onReloadDataTableMarketingPartnerListing(marketingListing)
            }
            onClose={this._onClosePartnerMarketListingEditFieldsModal}
          />
        )}
        {marketingPropertyListingEditDueDateModalProps && (
          <SrPartnerMarketListingEditDueDateModal
            {...marketingPropertyListingEditDueDateModalProps}
            dispatch={dispatch}
            reloadDataTableMarketingPartnerListing={() =>
              this._onReloadDataTableMarketingPartnerListing(marketingListing)
            }
            onClose={this._onClosePartnerMarketListingEditDueDateModal}
          />
        )}
      </>
    );
  }
}

SrPartnerListingPageListingFullService.displayName = 'SrPartnerListingPageListingFullService';

export default SrPartnerListingPageListingFullService;
